package com.eladfinish.bible.contest.screens.search.usecases.specialSearch

import com.eladfinish.bible.contest.common.usecases.RemoveNikudUseCase
import com.eladfinish.bible.contest.common.usecases.RemoveTeamimUseCase
import com.eladfinish.bible.contest.constants.StringConstants.MAKAF
import com.eladfinish.bible.contest.constants.StringConstants.REGEX_SPACE
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.extensions.runIf
import com.eladfinish.bible.contest.extensions.splitByRegex
import com.eladfinish.bible.contest.screens.search.model.ChapterDetails
import com.eladfinish.bible.contest.screens.search.model.VerseReference
import com.eladfinish.bible.contest.utils.toOtiot
import io.github.aakira.napier.Napier
import kotlinx.atomicfu.locks.SynchronizedObject
import kotlinx.atomicfu.locks.synchronized
import org.koin.core.component.KoinComponent
import org.koin.core.component.inject

/**
 * Developed by
 * @author Elad Finish
 */
data class Word(
    val text: String,
    val number: Int,
    val verseReference: VerseReference,
)

data class NumberReference(
    val number: Int,
    val verseReference: VerseReference?,
)

class WordIndex(
    private val includeLog: Boolean = false,
) : SpecialSearch,
    KoinComponent {

    private val sb = StringBuilder()
    private val wordMap = HashMap<String, NumberReference>()
    private val lock = SynchronizedObject()

    private val ignoreTeamim = true

    private val removeTeamimUseCase by inject<RemoveTeamimUseCase>()
    private val removeNikudUseCase by inject<RemoveNikudUseCase>()

    override fun process(chapterDetails: ChapterDetails) = with(chapterDetails) {
        verses.forEachIndexed { index, verse ->
            verse
                .trim()
                .replaceRegex(
                    regex = "$MAKAF",
                    replacement = "$MAKAF ",
                )
                .splitByRegex(REGEX_SPACE)
                .forEach { rawWord ->
                    val word = rawWord.runIf(ignoreTeamim) { removeTeamimExt() }

                    synchronized(lock) {
                        val current = wordMap[word] ?: NumberReference(
                            number = 0,
                            verseReference = null,
                        )

                        wordMap[word] = NumberReference(
                            number = current.number + 1,
                            verseReference = VerseReference(
                                bookName = bookName,
                                chapterName = chapterName,
                                verse = index + 1,
                            ),
                        )
                    }
                }
        }
    }

    override fun getSummary(): String {
        val sortedWords = sortMap()
        val result = sortedWords.joinToString { word ->
            "${word.text}=${word.number}"
        }

        Napier.d("wordMap $result")

        return sb.apply {
            clear()

            if (includeLog) {
                append("\n@@@\n")
            }

            append("כל המילים בתנ\"ך: ")
            append(wordMapSize())
            append("\n")
            append(result)
        }.toString()
    }

    override fun provideItems(): List<SpecialSearchItem> =
        sortMap().map { word ->
            val times = if (word.number == 1) {
                "פעם אחת"
            } else {
                "${word.number} פעמים"
            }

            SpecialSearchItem(
                content = "${word.text} - $times",
                book = word.verseReference.bookName.takeIf { word.number == 1 }.orEmpty(),
                chapter = word.verseReference.chapterName.takeIf { word.number == 1 }.orEmpty(),
                verse = word.verseReference.verse.toOtiot().takeIf { word.number == 1 }.orEmpty(),
            )
        }

    private fun sortMap(): List<Word> {
        val snapshot = synchronized(lock) {
            wordMap.toList()
        }

        return snapshot
            .map { (text, numberReference) ->
                Word(
                    text = text,
                    number = numberReference.number,
                    verseReference = requireNotNull(numberReference.verseReference),
                )
            }
            .sortedWith(
                compareBy<Word> { it.number }
                    .thenByDescending { removeNikudUseCase(pasuk = it.text) },
            )
            .reversed()
    }

    private fun wordMapSize(): Int =
        synchronized(lock) {
            wordMap.size
        }

    private fun String.removeTeamimExt(): String =
        removeTeamimUseCase(this)
}
